import React, { FunctionComponent, useMemo } from 'react';
import ManageNetworkMapContainer from './manage-network-map-container';
import {
  CityProViewBoolExp,
  ProViewBoolExp,
  useManageNetworkMapViewsQuery,
  useManageNetworkMapHeatmapQuery,
} from 'generated/graphql';

export type ManageNetworkMapFilters = {
  zoneIds: number[];
  tradeId?: number;
  onlyPresentationCategories?: boolean;
  displayHeatmap?: boolean;
  status?: 'published' | 'draft';
};

type ManageNetworkMapRepositoryProps = {
  filters: ManageNetworkMapFilters;
};

export const ManageNetworkMapRepository: FunctionComponent<
  ManageNetworkMapRepositoryProps
> = ({ filters }) => {
  const where = useMemo<ProViewBoolExp>(
    () => ({
      bilikZoneId: { _in: filters.zoneIds },
      status: {
        _in: filters.status ? [filters.status] : ['draft', 'published'],
      },
      ...(filters.tradeId && !filters.onlyPresentationCategories
        ? { trades: { tradeId: { _eq: filters.tradeId } } }
        : {}),
      ...(filters.tradeId && filters.onlyPresentationCategories
        ? {
            proPresentation: {
              mainTrades: { tradeId: { _eq: filters.tradeId } },
            },
          }
        : {}),
    }),
    [filters],
  );

  const { data, loading, error } = useManageNetworkMapViewsQuery({
    variables: { where },
  });

  const shouldDisplayHeatmap = !!filters.displayHeatmap && !!filters.tradeId;

  const heatmapWhere = useMemo<CityProViewBoolExp>(
    () => ({ proView: where }),
    [where],
  );

  const { data: heatmapData, loading: heatmapLoading } =
    useManageNetworkMapHeatmapQuery({
      variables: { where: heatmapWhere },
      skip: !shouldDisplayHeatmap,
    });

  return (
    <ManageNetworkMapContainer
      views={data?.proView ?? []}
      cities={shouldDisplayHeatmap ? (heatmapData?.city ?? []) : []}
      isLoading={loading}
      isHeatmapLoading={heatmapLoading && shouldDisplayHeatmap}
      error={error}
    />
  );
};
